import styled from 'styled-components'
import {
  StyledEditorContainerProps,
  StyledEditorAreaProps,
  StyledToolbarButtonProps,
  StyledCharCountProps,
} from './RichTextEditor.types'

export const StyledEditorContainer = styled.div<StyledEditorContainerProps>`
  display: flex;
  flex-direction: column;
  border: 1px solid
    ${({ theme, $error, $focused }) =>
      $error
        ? theme.colors.border.error
        : $focused
          ? theme.colors.border.focus
          : theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  background: ${({ theme }) => theme.colors.background.primary};
  transition: ${({ theme }) => theme.transitions.fast};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  &:hover {
    border-color: ${({ theme, $error, $focused }) =>
      !$error && !$focused && theme.colors.border.focus};
  }
`

export const StyledToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: ${({ theme }) => theme.spacing.small};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) =>
    `${theme.borderRadius.medium} ${theme.borderRadius.medium} 0 0`};
  flex-wrap: wrap;
  position: relative;
`

export const StyledToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.micro};
  position: relative;
`

export const StyledToolbarSeparator = styled.div`
  width: 1px;
  height: 24px;
  background: ${({ theme }) => theme.colors.border.default};
  margin: 0 ${({ theme }) => theme.spacing.xsmall};
`

export const StyledToolbarButton = styled.button<StyledToolbarButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primary : 'transparent'};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.text.inverse : theme.colors.text.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme, $active }) =>
      $active ? theme.colors.primaryHover : theme.colors.background.tertiary};
    color: ${({ theme, $active }) =>
      $active ? theme.colors.text.inverse : theme.colors.text.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`

export const StyledEditorArea = styled.div<StyledEditorAreaProps>`
  padding: ${({ theme }) => theme.spacing.medium};
  min-height: ${({ $minHeight }) =>
    typeof $minHeight === 'number' ? `${$minHeight}px` : $minHeight || '120px'};
  max-height: ${({ $maxHeight }) =>
    typeof $maxHeight === 'number' ? `${$maxHeight}px` : $maxHeight || 'none'};
  height: ${({ $height }) =>
    typeof $height === 'number' ? `${$height}px` : $height || 'auto'};
  overflow-y: auto;
  outline: none;
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  color: ${({ theme }) => theme.colors.text.primary};

  /* Tiptap editor styles */
  .ProseMirror {
    min-height: inherit;
    outline: none;

    &.ProseMirror-focused {
      outline: none;
    }

    p.is-editor-empty:first-child::before {
      content: attr(data-placeholder);
      float: left;
      color: ${({ theme }) => theme.colors.text.tertiary};
      pointer-events: none;
      height: 0;
    }

    /* Fix task list items - Tiptap structure */
    ul[data-type='taskList'] {
      list-style: none;
      padding: 0;
      margin: ${({ theme }) => theme.spacing.small} 0;

      li[data-type='taskItem'] {
        display: flex;
        align-items: flex-start;
        margin-bottom: ${({ theme }) => theme.spacing.xsmall};

        /* Hide any pseudo-element checkboxes */
        &::before {
          display: none !important;
        }

        label {
          display: flex;
          align-items: flex-start;
          margin-right: ${({ theme }) => theme.spacing.small};
          flex-shrink: 0;
          padding-top: 2px;

          input[type='checkbox'] {
            margin: 0;
            cursor: pointer;
          }
        }

        > div {
          flex: 1;
          line-height: 1.5;

          p {
            margin: 0;
            line-height: inherit;
          }
        }
      }
    }

    /* Disable checkbox interaction in read-only mode */
    &[contenteditable='false'] {
      ul[data-type='taskList'] {
        li[data-type='taskItem'] {
          label input[type='checkbox'] {
            pointer-events: none;
            opacity: 0.7;
          }
        }
      }
    }
  }

  h1 {
    font-size: ${({ theme }) => theme.typography.fontSize.hero};
    font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
    line-height: ${({ theme }) => theme.typography.lineHeight.tight};
    margin: ${({ theme }) => theme.spacing.medium} 0;
  }

  h2 {
    font-size: ${({ theme }) => theme.typography.fontSize.title};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    line-height: ${({ theme }) => theme.typography.lineHeight.tight};
    margin: ${({ theme }) => theme.spacing.medium} 0;
  }

  h3 {
    font-size: ${({ theme }) => theme.typography.fontSize.section};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    line-height: ${({ theme }) => theme.typography.lineHeight.normal};
    margin: ${({ theme }) => theme.spacing.default} 0;
  }

  h4 {
    font-size: ${({ theme }) => theme.typography.fontSize.section};
    font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
    line-height: ${({ theme }) => theme.typography.lineHeight.normal};
    margin: ${({ theme }) => theme.spacing.default} 0;
  }

  p {
    margin: ${({ theme }) => theme.spacing.small} 0;
  }

  blockquote {
    margin: ${({ theme }) => theme.spacing.medium} 0;
    padding-left: ${({ theme }) => theme.spacing.medium};
    border-left: 3px solid ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.text.secondary};
    font-style: italic;
  }

  code {
    background: ${({ theme }) => theme.colors.background.tertiary};
    padding: 2px 4px;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    font-family: ${({ theme }) => theme.typography.fontFamily.mono};
    font-size: ${({ theme }) => theme.typography.fontSize.small};
  }

  ul,
  ol {
    margin: ${({ theme }) => theme.spacing.small} 0;
    padding-left: ${({ theme }) => theme.spacing.large};
  }

  /* Remove duplicate task list styles that conflict with ProseMirror */

  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: underline;

    &:hover {
      color: ${({ theme }) => theme.colors.primaryHover};
    }
  }

  mark {
    background-color: yellow;
    padding: 0.125em 0;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    margin: ${({ theme }) => theme.spacing.small} 0;
  }
`

export const StyledFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) =>
    `0 0 ${theme.borderRadius.medium} ${theme.borderRadius.medium}`};
`

export const StyledCharCount = styled.span<StyledCharCountProps>`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
  color: ${({ theme, $exceeded }) =>
    $exceeded ? theme.colors.danger : theme.colors.text.tertiary};
`

export const StyledStyleDropdownWrapper = styled.div`
  margin-right: ${({ theme }) => theme.spacing.xsmall};
`

export const StyledHeadingDropdown = styled.select`
  background-color: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  padding: ${({ theme }) => theme.spacing.xsmall}
    ${({ theme }) => theme.spacing.small};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  outline: none;
  transition: all 0.2s;
  height: 32px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary}20;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
